// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import userSvc from "../../../services/api/userManagement";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import FormInput from "../../../components/form-input/FormInput";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";

// --- Models ---
import { userSchema } from "../../../models/schema/user";
import { UserItem } from "../../../models/types/user/user-managerment";

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import FormSelect from "../../../components/form-input/formSelect";
import FormTextarea from "../../../components/form-input/FormTextarea";
import FormDate from "../../../components/form-input/formDate";
import dayjs from "dayjs";
import { listGender } from "../../../utils/configuration";
import { useLookupRole } from "../../../utils/get-lookup";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: UserItem | null;
}

export default function ProblemCreateUpdate({ isCreate, data }: FormProps) {
  const navigate = useNavigate();

  const { lookupRole } = useLookupRole();

  const [loading, setLoading] = useState(false);

  const schema = userSchema;

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: "",
      firstName: "",
      lastName: "",
      email: "",
      dateOfBirth: "",
      sex: false,
      hometown: "",
      city: "",
      country: "",
      phoneNumber: "",
      description: "",
      roleId: "",
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ...omitMetaFields(data),
        dateOfBirth: dayjs(data.dateOfBirth).format("YYYY-MM-DD"),
      });
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await userSvc.createUser(values) : await userSvc.updateUser(values);
      if (res && res.content.status) {
        unlock();
        notify(t("common.success"), isCreate ? t("userManagement.addSuccess") : t("userManagement.updateSuccess"), "success");
        setLoading(false);
        navigate(UrlCollection.UserManager);
      }
    } catch (e: any) {
      notify(t("common.error"), t("common.errorOccurred"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm={t("common.confirm")}
        action={confirmLeave}
      />
      <div className="w-full h-full">
        <ButtonGoback />
        <div className="w-full mx-auto p-8 bg-white rounded-lg shadow mt-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
              <FormInput
                label={t("userManagement.table.fullName")}
                name="fullName"
                register={register("fullName")}
                error={errors.fullName}
                required
              />
              <FormInput
                label={t("roles.firstName")}
                name="firstName"
                register={register("firstName")}
                error={errors.firstName}
              />
              <FormInput
                label={t("roles.lastName")}
                name="lastName"
                register={register("lastName")}
                error={errors.lastName}
              />
              <FormInput
                label="Email"
                name="email"
                type="email"
                register={register("email")}
                error={errors.email}
              />
              <FormSelect
                label={t("roles.role")}
                options={lookupRole}
                name="roleId"
                control={control}
                errors={errors.roleId}
              />
              <FormDate
                label={t("roles.dateOfBirth")}
                name="dateOfBirth"
                control={control}
                outputFormat="YYYY-MM-DD"
                errors={errors}
              />
              <FormSelect
                label={t("roles.gender")}
                options={listGender}
                name="sex"
                control={control}
                errors={errors.sex}
              />
              <FormInput
                label={t("roles.hometown")}
                name="hometown"
                register={register("hometown")}
                error={errors.hometown}
              />
              <FormInput
                label={t("roles.city")}
                name="city"
                register={register("city")}
                error={errors.city}
              />
              <FormInput
                label={t("roles.country")}
                name="country"
                register={register("country")}
                error={errors.country}
              />
              <FormInput
                label={t("roles.phoneNumber")}
                name="phoneNumber"
                register={register("phoneNumber")}
                error={errors.phoneNumber}
              />
              <FormTextarea
                label={t("roles.description")}
                name="description"
                register={register("description")}
                error={errors.description}
                rows={5}
              />
              {/* <div className="">
                <FormDropzone
                  setValue={(files: FileItem[]) => setValue('file', files[0].id, { shouldDirty: true })}
                  label="Avatar"
                  multiple={false}
                  accept={{
                    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                  }}
                  maxSize={10 * 1024 * 1024} // 10MB per file
                  helperText="Upload PDF or Word documents (max 10MB each)."
                  defaultValue={[]}
                />
              </div> */}
            </div>
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit mt-6"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {isCreate ? t("roles.addNew") : t("roles.update")}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}